import { cn } from '@/lib/cn';
import { formatMoney, formatWager } from '../format';
import type { Standing } from '../api';

interface LeaderboardTableProps {
  rows: Standing[];
  currency: string;
  /** The caller's own row, pinned when it falls outside the rows shown. */
  you?: Standing | null | undefined;
}

/**
 * The leaderboard rows it is given.
 *
 * A real table, because it is one: four columns of aligned data that a screen
 * reader should be able to navigate by column. The member's own row is
 * highlighted and, when it sits outside the rows shown, pinned beneath — the
 * detail page lists only the paying places, so a member outside them would
 * otherwise have no way to see where they stand.
 */
export function LeaderboardTable({ rows, currency, you }: LeaderboardTableProps) {
  const youAlreadyShown = you ? rows.some((row) => row.rank === you.rank && row.isYou) : false;

  return (
    <div className="overflow-x-auto">
      <table className="leaderboard-table w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-surface-800 text-left text-xs uppercase tracking-widest text-surface-500">
            <th scope="col" className="w-16 px-3 py-3 font-medium">
              Sıra
            </th>
            <th scope="col" className="px-3 py-3 font-medium">
              Oyuncu
            </th>
            <th scope="col" className="px-3 py-3 text-right font-medium">
              Çevrim
            </th>
            <th scope="col" className="px-3 py-3 text-right font-medium">
              Ödül
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <Row key={`${row.rank}-${row.displayName}`} row={row} currency={currency} />
          ))}

          {you && !youAlreadyShown ? (
            <>
              <tr>
                <td colSpan={4} className="px-3 py-2 text-center text-xs text-surface-600">
                  ⋯
                </td>
              </tr>
              <Row row={you} currency={currency} />
            </>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function Row({ row, currency }: { row: Standing; currency: string }) {
  return (
    <tr
      className={cn(
        'border-b border-surface-900 transition',
        row.isYou ? 'bg-brand-500/10' : 'hover:bg-surface-900/60',
      )}
    >
      <td className="px-3 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'font-mono font-semibold tabular-nums',
              row.rank === 1
                ? 'text-brand-400'
                : row.rank <= 3
                  ? 'text-surface-100'
                  : 'text-surface-400',
            )}
          >
            {row.rank}
          </span>
          <Movement value={row.movement} />
        </div>
      </td>

      <td className="px-3 py-3">
        <div className="leaderboard-player flex min-w-0 items-center gap-2.5">
          {row.avatarUrl ? (
            <img
              src={row.avatarUrl}
              alt=""
              width={28}
              height={28}
              loading="lazy"
              className="size-7 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-surface-800 text-xs font-bold text-surface-500">
              {row.displayName.slice(0, 1).toUpperCase()}
            </span>
          )}

          <span
            className={cn(
              'truncate',
              row.isYou ? 'font-medium text-brand-400' : 'text-surface-200',
            )}
          >
            {row.displayName}
          </span>

          {row.isYou ? (
            <span className="rounded-full bg-brand-500/20 px-1.5 py-0.5 text-xs font-bold text-brand-400">
              SEN
            </span>
          ) : null}
        </div>
      </td>

      <td className="px-3 py-3 text-right font-mono text-surface-300 tabular-nums">
        {formatWager(row.wagered, currency)}
      </td>

      <td className="px-3 py-3 text-right">
        {row.prize !== null && row.prize !== undefined ? (
          <span className="font-medium text-brand-400">
            {row.prizeLabel ?? formatMoney(row.prize, currency)}
          </span>
        ) : (
          <span className="text-surface-700">—</span>
        )}
      </td>
    </tr>
  );
}

/**
 * Places gained since the last import.
 *
 * The arrow is decorative and the number carries the meaning, so the accessible
 * text spells it out — a bare "▲2" announced as "black up-pointing triangle 2"
 * is not information.
 */
function Movement({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined || value === 0) {
    return null;
  }

  const up = value > 0;

  return (
    <span
      className={cn('text-xs font-semibold', up ? 'text-emerald-400' : 'text-red-400')}
      title={up ? `${value} sıra yükseldi` : `${Math.abs(value)} sıra düştü`}
    >
      <span aria-hidden>{up ? '▲' : '▼'}</span>
      <span className="sr-only">{up ? 'yükseldi' : 'düştü'}</span>
      {Math.abs(value)}
    </span>
  );
}
